import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col } from "reactstrap";
import axios from "axios";
import { toast } from "react-toastify";

// ─── Inject animations ────────────────────────────────────────────────────────
if (typeof document !== "undefined" && !document.getElementById("up-anim")) {
  const s = document.createElement("style");
  s.id = "up-anim";
  s.textContent = `
    @keyframes fadeInUp { from { opacity:0; transform:translateY(20px) } to { opacity:1; transform:translateY(0) } }
    @keyframes fadeIn   { from { opacity:0 } to { opacity:1 } }
    @keyframes slideUp  { from { opacity:0; transform:translateY(28px) } to { opacity:1; transform:translateY(0) } }
    @keyframes spin     { to { transform: rotate(360deg) } }
    .up-card { animation: fadeInUp 0.4s ease both; }
    .up-card:nth-child(2) { animation-delay: 0.08s }
    .up-card:nth-child(3) { animation-delay: 0.16s }
  `;
  document.head.appendChild(s);
}

// ─── Theme ────────────────────────────────────────────────────────────────────
const C = {
  primary: "#02a499",
  primaryDark: "#028f83",
  primaryLight: "#e6f7f6",
  danger: "#e53e3e",
  text: "#1a202c",
  muted: "#718096",
  border: "#e2e8f0",
  bg: "#f7fafc",
  white: "#ffffff",
  success: "#38a169",
};

// ─── Shared popup styles ──────────────────────────────────────────────────────
const ps = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    backdropFilter: "blur(4px)", zIndex: 1050,
    display: "flex", alignItems: "center", justifyContent: "center",
    animation: "fadeIn 0.18s ease",
  },
  card: {
    background: C.white, borderRadius: 18, padding: "32px 28px 24px",
    width: "100%", maxWidth: 380, boxShadow: "0 24px 64px rgba(0,0,0,0.16)",
    position: "relative", animation: "slideUp 0.22s ease",
  },
  closeBtn: { position: "absolute", top: 14, right: 16, background: "none", border: "none", fontSize: 22, color: "#bbb", cursor: "pointer", lineHeight: 1 },
  iconWrap: (color) => ({ width: 52, height: 52, borderRadius: "50%", background: color || `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }),
  title: { fontSize: 17, fontWeight: 700, color: C.text, textAlign: "center", marginBottom: 6 },
  subtitle: { fontSize: 13, color: C.muted, textAlign: "center", marginBottom: 22, lineHeight: 1.6 },
  input: { width: "100%", padding: "10px 14px", borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 14, color: C.text, outline: "none", marginBottom: 4, boxSizing: "border-box" },
  inputErr: { borderColor: C.danger },
  errText: { color: C.danger, fontSize: 12, marginBottom: 10 },
  btnRow: { display: "flex", gap: 10, marginTop: 6 },
  btnPrimary: { flex: 1, padding: "10px 0", borderRadius: 8, background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, color: "#fff", border: "none", fontWeight: 700, fontSize: 14, cursor: "pointer" },
  btnSecondary: { flex: 1, padding: "10px 0", borderRadius: 8, background: "#f1f5f9", color: "#4a5568", border: "none", fontWeight: 600, fontSize: 14, cursor: "pointer" },
  otpBox: { width: 44, height: 50, textAlign: "center", fontSize: 20, fontWeight: 700, borderRadius: 8, border: `1.5px solid ${C.border}`, outline: "none", background: "#f8fafc", color: C.text, transition: "border-color 0.15s" },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const Avatar = ({ name }) => {
  const initials = (name || "U").split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
  return (
    <div style={{ width: 88, height: 88, borderRadius: "50%", background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30, fontWeight: 700, color: "#fff", boxShadow: `0 8px 24px ${C.primary}55`, flexShrink: 0 }}>
      {initials}
    </div>
  );
};

const InfoRow = ({ icon, label, value }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "14px 0", borderBottom: `1px solid ${C.border}` }}>
    <div style={{ width: 36, height: 36, borderRadius: 10, background: C.primaryLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <i className={icon} style={{ color: C.primary, fontSize: 16 }} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, color: C.text, fontWeight: 500, wordBreak: "break-word" }}>
        {value || <span style={{ color: C.muted, fontStyle: "italic" }}>Not provided</span>}
      </div>
    </div>
  </div>
);

const Badge = ({ verified }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: verified ? "#f0fff4" : "#fff5f5", color: verified ? C.success : C.danger, border: `1px solid ${verified ? "#9ae6b4" : "#feb2b2"}` }}>
    <i className={verified ? "ri-shield-check-line" : "ri-shield-cross-line"} style={{ fontSize: 12 }} />
    {verified ? "Verified" : "Unverified"}
  </span>
);

// ─── Change Email Popup (simple PUT, no OTP) ──────────────────────────────────
const ChangeEmailPopup = ({ currentEmail, userId, onClose, onSuccess }) => {
  const [step, setStep] = useState("confirm"); // confirm | form | success
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    if (!newEmail) { setEmailError("Please enter an email address"); return; }
    if (!/\S+@\S+\.\S+/.test(newEmail)) { setEmailError("Please enter a valid email"); return; }
    setEmailError("");
    setIsLoading(true);
    try {
      await axios.put(`https://api.hanoch.in/users/change-email/${userId}/`, { email: newEmail });
      toast.success("Email updated successfully!");
      setStep("success");
      onSuccess(newEmail);
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.email?.[0] || "Failed to update email");
    } finally { setIsLoading(false); }
  };

  return (
    <div style={ps.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={ps.card}>
        <button style={ps.closeBtn} onClick={onClose}>×</button>

        {step === "confirm" && (
          <>
            <div style={ps.iconWrap()}><i className="ri-mail-line" style={{ color: "#fff", fontSize: 22 }} /></div>
            <div style={ps.title}>Change Email Address?</div>
            <div style={ps.subtitle}>Current email:<br /><strong style={{ color: C.text }}>{currentEmail}</strong><br />Would you like to update it?</div>
            <div style={ps.btnRow}>
              <button style={ps.btnSecondary} onClick={onClose}>Not Now</button>
              <button style={ps.btnPrimary} onClick={() => setStep("form")}>Yes, Change</button>
            </div>
          </>
        )}

        {step === "form" && (
          <>
            <div style={ps.iconWrap()}><i className="ri-mail-edit-line" style={{ color: "#fff", fontSize: 22 }} /></div>
            <div style={ps.title}>Enter New Email</div>
            <div style={ps.subtitle}>Enter the new email address for your account.</div>
            <input
              autoFocus type="email" placeholder="New email address" value={newEmail}
              onChange={(e) => { setNewEmail(e.target.value); setEmailError(""); }}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              style={{ ...ps.input, ...(emailError ? ps.inputErr : {}) }}
            />
            {emailError && <div style={ps.errText}>{emailError}</div>}
            <div style={ps.btnRow}>
              <button style={ps.btnSecondary} onClick={() => setStep("confirm")}>Back</button>
              <button style={{ ...ps.btnPrimary, opacity: isLoading ? 0.75 : 1 }} onClick={handleSubmit} disabled={isLoading}>
                {isLoading ? <span className="spinner-border spinner-border-sm" /> : "Save"}
              </button>
            </div>
          </>
        )}

        {step === "success" && (
          <>
            <div style={ps.iconWrap(`linear-gradient(135deg, ${C.success}, #2f855a)`)}>
              <i className="ri-check-line" style={{ color: "#fff", fontSize: 26 }} />
            </div>
            <div style={ps.title}>Email Updated!</div>
            <div style={ps.subtitle}>Your email has been changed to<br /><strong style={{ color: C.primary }}>{newEmail}</strong></div>
            <button style={{ ...ps.btnPrimary, width: "100%" }} onClick={onClose}>Done</button>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Verify Email Popup (resend-otp → verify-otp, with popup steps) ───────────
const VerifyEmailPopup = ({ email, onClose, onVerified }) => {
  const [step, setStep] = useState("sending"); // sending | otp | success
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [cooldownMsg, setCooldownMsg] = useState("");
  const otpRefs = useRef([]);

  // Send OTP on mount
  useEffect(() => {
    sendOtp();
  }, []);

  useEffect(() => {
    if (resendTimer > 0) {
      const t = setTimeout(() => setResendTimer((v) => v - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendTimer]);

  const sendOtp = async () => {
    setIsLoading(true);
    setCooldownMsg("");
    try {
      await axios.post("https://api.hanoch.in/users/resend-otp/", { email });
      toast.success("OTP sent to your email!");
      setStep("otp");
      setResendTimer(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 120);
    } catch (err) {
      const errMsg = err.response?.data?.error || "";
      // Parse cooldown seconds from error message if present
      const match = errMsg.match(/wait (\d+) second/);
      if (match) {
        setResendTimer(parseInt(match[1]));
        setCooldownMsg(errMsg);
        setStep("otp"); // still show OTP step in case they have a previous code
      } else {
        toast.error(errMsg || "Failed to send OTP");
        onClose();
      }
    } finally { setIsLoading(false); }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setIsLoading(true);
    setCooldownMsg("");
    try {
      await axios.post("https://api.hanoch.in/users/resend-otp/", { email });
      toast.success("OTP resent!");
      setOtp(["", "", "", "", "", ""]);
      setResendTimer(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 80);
    } catch (err) {
      const errMsg = err.response?.data?.error || "";
      const match = errMsg.match(/wait (\d+) second/);
      if (match) { setResendTimer(parseInt(match[1])); setCooldownMsg(errMsg); }
      else toast.error(errMsg || "Failed to resend OTP");
    } finally { setIsLoading(false); }
  };

  const handleVerify = async () => {
    const val = otp.join("");
    if (val.length < 6) { setOtpError("Please enter the complete 6-digit OTP"); return; }
    setOtpError("");
    setIsLoading(true);
    try {
      await axios.post("https://api.hanoch.in/users/verify-otp/", { email, otp: val });
      setStep("success");
      onVerified();
    } catch (err) {
      setOtpError(err.response?.data?.message || err.response?.data?.error || "Invalid OTP. Please try again.");
      setOtp(["", "", "", "", "", ""]);
      setTimeout(() => otpRefs.current[0]?.focus(), 80);
    } finally { setIsLoading(false); }
  };

  const handleOtpChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const u = [...otp]; u[i] = val.slice(-1); setOtp(u); setOtpError("");
    if (val && i < 5) otpRefs.current[i + 1]?.focus();
  };
  const handleOtpKey = (i, e) => { if (e.key === "Backspace" && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus(); };
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const p = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!p) return;
    const u = [...otp]; p.split("").forEach((c, i) => { u[i] = c; }); setOtp(u);
    const n = u.findIndex((v) => !v); otpRefs.current[n === -1 ? 5 : n]?.focus();
  };

  return (
    <div style={ps.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={ps.card}>
        <button style={ps.closeBtn} onClick={onClose}>×</button>

        {/* Sending state */}
        {step === "sending" && (
          <>
            <div style={ps.iconWrap()}><i className="ri-mail-send-line" style={{ color: "#fff", fontSize: 22 }} /></div>
            <div style={ps.title}>Sending OTP…</div>
            <div style={{ textAlign: "center", paddingTop: 8 }}>
              <span className="spinner-border" style={{ color: C.primary, width: 28, height: 28 }} />
            </div>
          </>
        )}

        {/* OTP entry */}
        {step === "otp" && (
          <>
            <div style={ps.iconWrap()}><i className="ri-shield-keyhole-line" style={{ color: "#fff", fontSize: 22 }} /></div>
            <div style={ps.title}>Verify Your Email</div>
            <div style={ps.subtitle}>Enter the 6-digit code sent to<br /><strong style={{ color: C.text }}>{email}</strong></div>

            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 16 }} onPaste={handleOtpPaste}>
              {otp.map((d, i) => (
                <input key={i} ref={(el) => (otpRefs.current[i] = el)}
                  type="text" inputMode="numeric" maxLength={1} value={d}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKey(i, e)}
                  style={{ ...ps.otpBox, ...(d ? { borderColor: C.primary } : {}) }} />
              ))}
            </div>

            {otpError && <div style={{ ...ps.errText, textAlign: "center" }}>{otpError}</div>}

            <div style={{ fontSize: 12, color: C.muted, textAlign: "center", marginBottom: 14 }}>
              {resendTimer > 0
                ? <>{cooldownMsg || <>Resend in <strong>{resendTimer}s</strong></>}</>
                : <>Didn't get it?{" "}
                    <button style={{ background: "none", border: "none", color: C.primary, fontWeight: 700, fontSize: 12, cursor: "pointer", padding: 0 }} onClick={handleResend} disabled={isLoading}>
                      Resend OTP
                    </button>
                  </>
              }
            </div>

            <button
              style={{ ...ps.btnPrimary, width: "100%", opacity: isLoading ? 0.75 : 1 }}
              onClick={handleVerify} disabled={isLoading}>
              {isLoading ? <span className="spinner-border spinner-border-sm" /> : "Verify Email"}
            </button>
          </>
        )}

        {/* Success */}
        {step === "success" && (
          <>
            <div style={ps.iconWrap(`linear-gradient(135deg, ${C.success}, #2f855a)`)}>
              <i className="ri-shield-check-line" style={{ color: "#fff", fontSize: 26 }} />
            </div>
            <div style={ps.title}>Email Verified!</div>
            <div style={ps.subtitle}>Your email <strong style={{ color: C.text }}>{email}</strong> has been successfully verified.</div>
            <button style={{ ...ps.btnPrimary, width: "100%" }} onClick={onClose}>Done</button>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const UserProfile = () => {
  document.title = "My Profile";
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const id = localStorage.getItem("id");
        const response = await fetch(`https://api.hanoch.in/users/users/${id}`);
        if (response.ok) {
          const data = await response.json();
          setUserData(data?.data || data);
        } else if (response.status === 401) {
          localStorage.removeItem("authUser");
        } else {
          console.error("Error fetching user:", response.statusText);
        }
      } catch (err) {
        console.error("Error fetching user:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  const fullName = userData ? `${userData.first_name || ""} ${userData.last_name || ""}`.trim() : "";
  const userId = localStorage.getItem("id");

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: C.muted }}>
          <div style={{ width: 40, height: 40, border: `3px solid ${C.border}`, borderTopColor: C.primary, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
          <div style={{ fontSize: 14 }}>Loading profile…</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{ minHeight: "100vh", paddingTop: 80, paddingBottom: 40,marginBottom:40, background: C.bg }}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={7} md={9} xs={12}>

              {/* ── Header card ── */}
              <div className="up-card" style={{ background: C.white, borderRadius: 20, padding: "28px 28px 22px", boxShadow: "0 8px 32px rgba(0,0,0,0.10)", marginBottom: 18 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
                  <Avatar name={fullName} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 4 }}>{fullName || "—"}</div>
                    <div style={{ fontSize: 13, color: C.muted, marginBottom: 8 }}>
                      {userData?.role === "quality_manager" ? "Engineer" : userData?.role || "—"}
                    </div>
                    <Badge verified={userData?.is_verified} />
                  </div>
                </div>
              </div>

              {/* ── Details card ── */}
              <div className="up-card" style={{ background: C.white, borderRadius: 20, padding: "8px 28px 16px", boxShadow: "0 8px 32px rgba(0,0,0,0.08)", marginBottom: 18 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", padding: "16px 0 4px" }}>Account Details</div>

                <InfoRow icon="ri-user-3-line" label="First Name" value={userData?.first_name} />
                <InfoRow icon="ri-user-3-line" label="Last Name" value={userData?.last_name} />
                <InfoRow icon="ri-phone-line" label="Phone" value={userData?.phone || userData?.mobile} />
                <InfoRow icon="ri-building-line" label="Company" value={userData?.company_name || userData?.company} />
                <InfoRow icon="ri-shield-user-line" label="Role" value={userData?.role === "quality_manager" ? "Engineer" : userData?.role} />

                {/* ── Email row ── */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "14px 0" }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: C.primaryLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <i className="ri-mail-line" style={{ color: C.primary, fontSize: 16 }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>Email</div>
                    <div style={{ fontSize: 14, color: C.text, fontWeight: 500, wordBreak: "break-word", marginBottom: 10 }}>
                      {userData?.email || <span style={{ color: C.muted, fontStyle: "italic" }}>Not provided</span>}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <Badge verified={userData?.is_verified} />

                      {/* Verify button — only if not verified */}
                      {!userData?.is_verified && (
                        <button
                          onClick={() => setShowVerifyEmail(true)}
                          style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 12px", borderRadius: 20, border: `1.5px solid ${C.primary}`, background: "transparent", color: C.primary, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                          <i className="ri-mail-send-line" /> Verify Email
                        </button>
                      )}

                      {/* Change email — always visible */}
                      <button
                        onClick={() => setShowChangeEmail(true)}
                        style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 12px", borderRadius: 20, border: `1.5px solid ${C.border}`, background: "transparent", color: C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                        <i className="ri-edit-line" /> Change Email
                      </button>
                    </div>
                  </div>
                </div>

              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Change Email Popup */}
      {showChangeEmail && (
        <ChangeEmailPopup
          currentEmail={userData?.email}
          userId={userId}
          onClose={() => setShowChangeEmail(false)}
          onSuccess={(newEmail) => {
            setUserData((prev) => ({ ...prev, email: newEmail, is_verified: false }));
            setShowChangeEmail(false);
          }}
        />
      )}

      {/* Verify Email Popup */}
      {showVerifyEmail && (
        <VerifyEmailPopup
          email={userData?.email}
          onClose={() => setShowVerifyEmail(false)}
          onVerified={() => setUserData((prev) => ({ ...prev, is_verified: true }))}
        />
      )}
    </>
  );
};

export default UserProfile;
